// YouTube OAuth callback landing page.
// Users arrive here after Google redirects them back through
// /api/brand-os/publish/oauth/youtube/callback. This page:
//   1. Reads the `session` and optional `error` params from the URL.
//   2. Fetches the pending OAuth session to render a Channel picker.
//   3. POSTs the finalize action; on success bounces back to Channel
//      Connections admin with a success flash.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

function humanizeError(code) {
  const m = {
    missing_code_or_state: "The redirect from Google was missing the code or state parameter.",
    state_expired: "The connect flow timed out before it finished. Please start again.",
    state_malformed: "Something altered the OAuth state. Please start the connect flow again.",
    state_invalid_signature: "The OAuth state failed its signature check. Please start the connect flow again.",
    state_channel_mismatch: "That OAuth state wasn't issued for YouTube.",
    code_exchange_failed: "Google refused the authorization code. Please try the connect flow again.",
    userinfo_failed: "Google accepted the token but refused to describe the account.",
    channels_listing_failed: "Google returned an error listing your YouTube channels.",
    google_unreachable: "We couldn't reach Google. Try again in a minute.",
  };
  return m[code] || code;
}

export default function YouTubeOAuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session") || "";
  const errorFromGoogle = params.get("error");

  const [session, setSession] = useState(null);
  const [pickedChannelId, setPickedChannelId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [isDefault, setIsDefault] = useState(true);
  const [loadState, setLoadState] = useState({ loading: true, error: null });
  const [submitState, setSubmitState] = useState({ submitting: false, error: null });

  const bounceBack = useCallback((flash, err) => {
    const search = new URLSearchParams();
    if (flash) search.set("oauth_result", encodeURIComponent(flash));
    if (err) search.set("oauth_error", encodeURIComponent(err));
    navigate({
      pathname: "/brand-os/admin/channel-connections",
      search: `?${search.toString()}`,
    }, { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (errorFromGoogle) {
      setLoadState({ loading: false, error: humanizeError(errorFromGoogle) });
      return;
    }
    if (!sessionId) {
      setLoadState({ loading: false, error: "Missing session id in URL." });
      return;
    }
    (async () => {
      try {
        const r = await api.get(`/publish/oauth/youtube/session/${sessionId}`);
        setSession(r.data);
        const channels = r.data.pages || [];
        if (channels.length === 1) {
          setPickedChannelId(String(channels[0].id));
          setDisplayName(channels[0].title ? `YouTube · ${channels[0].title}` : "");
        }
        setLoadState({ loading: false, error: null });
      } catch (e) {
        setLoadState({ loading: false, error: formatApiError(e) });
      }
    })();
  }, [sessionId, errorFromGoogle]);

  useEffect(() => {
    const ch = (session?.pages || []).find(
      (c) => String(c.id) === pickedChannelId,
    );
    if (ch && !displayName.trim().startsWith("YouTube ·")) {
      setDisplayName(`YouTube · ${ch.title || ch.id}`);
    }
  }, [pickedChannelId, session, displayName]);

  const channels = session?.pages || [];
  const diagnostic = session?.diagnostic || {};
  const testMode = !!session?.diagnostic?.test_mode || !!session?.test_mode;

  async function handleFinalize() {
    if (!pickedChannelId) return;
    setSubmitState({ submitting: true, error: null });
    try {
      const r = await api.post("/publish/oauth/youtube/finalize", {
        session_id: sessionId,
        channel_id: pickedChannelId,
        display_name: displayName.trim(),
        is_default: isDefault,
      });
      const name = r.data?.connection?.display_name || "YouTube channel";
      bounceBack(`Connected ${name}. Uploads land in TEST_MODE until Google audit clears.`);
    } catch (e) {
      setSubmitState({ submitting: false, error: formatApiError(e) });
    }
  }

  if (loadState.loading) {
    return (
      <Shell title="Connect YouTube">
        <LoadingCard label="Loading the YouTube OAuth session…" />
      </Shell>
    );
  }

  return (
    <Shell title="Connect YouTube">
      <div className="bo-page" data-testid="yt-oauth-callback">
        <section className="bo-card">
          <div className="bo-eyebrow">Publish → Channel Connections → YouTube</div>
          <h1 className="bo-serif" style={{ fontSize: 26, margin: "6px 0 8px" }}>
            Pick the YouTube channel to connect
          </h1>
          <p style={{ color: "var(--bo-muted)", maxWidth: 760, lineHeight: 1.55 }}>
            Google issued a short-lived access token and a durable refresh token.
            Pick the Precipice Health Brand Account channel, and Brand OS will
            store both encrypted. Scheduled uploads automatically refresh when
            the access token expires.
          </p>

          {testMode ? (
            <Pill data-testid="yt-test-mode-pill" style={{ marginTop: 8 }}>
              TEST_MODE — Google client id is empty so this flow uses a synthetic
              channel. Paste real values into <code>YOUTUBE_OAUTH_CLIENT_ID</code> and
              <code>YOUTUBE_OAUTH_CLIENT_SECRET</code> in the Secrets panel to go live.
            </Pill>
          ) : null}

          {loadState.error ? (
            <ErrorBanner data-testid="yt-oauth-error">{loadState.error}</ErrorBanner>
          ) : null}

          {diagnostic.refresh_token_warning ? (
            <ErrorBanner data-testid="yt-refresh-warning" tone="warn">
              {diagnostic.refresh_token_warning}
            </ErrorBanner>
          ) : null}

          {channels.length === 0 ? (
            <EmptyState
              title="No owned YouTube channels on this Google account"
              detail={diagnostic.diagnosis
                || "Sign in with the Google account that owns the Precipice Brand Account."}
              data-testid="yt-no-channels"
            />
          ) : (
            <>
              <div style={{ display: "grid", gap: 8, margin: "16px 0" }}>
                {channels.map((ch) => (
                  <label
                    key={ch.id}
                    data-testid={`yt-channel-opt-${ch.id}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: 12,
                      border: "1px solid var(--bo-border)",
                      borderRadius: 6,
                      cursor: "pointer",
                      background:
                        pickedChannelId === String(ch.id)
                          ? "var(--bo-panel-alt)"
                          : "transparent",
                    }}
                  >
                    <input
                      type="radio"
                      name="yt-channel"
                      value={ch.id}
                      checked={pickedChannelId === String(ch.id)}
                      onChange={(e) => setPickedChannelId(e.target.value)}
                    />
                    {ch.thumbnail_url ? (
                      <img
                        src={ch.thumbnail_url}
                        alt=""
                        width={48}
                        height={48}
                        style={{ borderRadius: "50%" }}
                      />
                    ) : null}
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <strong>{ch.title}</strong>
                      <span style={{ fontSize: 12, color: "var(--bo-muted)" }}>
                        {ch.custom_url || ch.id}
                        {" · "}
                        {ch.subscriber_count ?? 0} subscribers
                      </span>
                    </div>
                  </label>
                ))}
              </div>

              <div className="bo-field" style={{ marginTop: 8 }}>
                <label className="bo-label" htmlFor="yt-display-name">
                  Connection display name
                </label>
                <input
                  id="yt-display-name"
                  className="bo-input"
                  data-testid="yt-display-name-input"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="YouTube · Precipice Health"
                />
              </div>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 10,
                  fontSize: 14,
                }}
              >
                <input
                  type="checkbox"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  data-testid="yt-is-default-checkbox"
                />
                Make this the default YouTube connection for scheduled uploads
              </label>

              {submitState.error ? (
                <ErrorBanner data-testid="yt-finalize-error">
                  {submitState.error}
                </ErrorBanner>
              ) : null}

              <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
                <button
                  className="bo-btn bo-btn-primary"
                  data-testid="yt-finalize-btn"
                  onClick={handleFinalize}
                  disabled={!pickedChannelId || submitState.submitting}
                >
                  {submitState.submitting ? "Connecting…" : "Connect this channel"}
                </button>
                <button
                  className="bo-btn bo-btn-ghost"
                  data-testid="yt-cancel-btn"
                  onClick={() => bounceBack(null, "User cancelled")}
                  disabled={submitState.submitting}
                >
                  Cancel
                </button>
              </div>
            </>
          )}

          <details style={{ marginTop: 24 }}>
            <summary
              style={{ cursor: "pointer", color: "var(--bo-muted)", fontSize: 13 }}
            >
              Diagnostic details
            </summary>
            <pre
              style={{
                fontSize: 12,
                background: "var(--bo-panel-alt)",
                padding: 10,
                borderRadius: 4,
                overflow: "auto",
              }}
            >
              {JSON.stringify(
                {
                  session_id: sessionId,
                  channel: session?.channel,
                  state: session?.state,
                  granted_scopes: diagnostic.granted_scopes,
                  required_scopes: diagnostic.required_scopes,
                  channels_returned: diagnostic.channels_returned,
                  google_user_email: diagnostic.google_email,
                  refresh_token_present: diagnostic.refresh_token_present,
                },
                null,
                2,
              )}
            </pre>
          </details>
        </section>
      </div>
    </Shell>
  );
}
